import React, { forwardRef } from 'react'
import dayjs from 'dayjs'
import { Card, CardHeader, Divider } from '@mui/material'

import { PropertyList } from '../../../../../components/PropertyList'
import { PropertyListItem } from '../../../../../components/PropertyListItem'
import CircularLoading from '../../../../../components/CircularProgress'
import { ICostDailyReport, IMerchantDailyReport } from '../../DailySalesReport'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { mergeObjects, numberFormat } from '../../../../../utils'
import { sumBy } from 'lodash'
import { useUser } from '../../../../../hooks/useUser'

interface DailyReportDetailProps {
  isLoading: boolean
  merchantDailyReports: IMerchantDailyReport[]
  costDailyReport: ICostDailyReport[]
}

interface IReport extends IMerchantDailyReport {
  costAmountTotal: number
}

const DailyReportDetail = forwardRef<HTMLTableElement, DailyReportDetailProps>(
  (props: DailyReportDetailProps, ref) => {
    const { isLoading, merchantDailyReports, costDailyReport } = props
    const { user } = useUser()


    const costObject = costDailyReport?.reduce(
      (acc: any, costDailyReportItem: ICostDailyReport) => {
        const date = dayjs(costDailyReportItem.date).format(ONLY_DATE_FORMAT)
        acc[date] = {
          costAmountTotal: costDailyReportItem.amount,
        }
        return acc
      },
      {},
    )

    const merchantObject = merchantDailyReports?.reduce(
      (acc: any, merchantDailyReportItem: IMerchantDailyReport) => {
        const startDate = merchantDailyReportItem.date.split('T')?.[0] + 'T00:00:00'
        const date = dayjs(startDate).format(ONLY_DATE_FORMAT)
        acc[date] = merchantDailyReportItem
        return acc
      },
      {},
    )

    const reports = mergeObjects(costObject, merchantObject)

    const reportsArray: IReport[] = Object.keys(reports).map(
      (date: string, index: number) => {
        const merchantDailyReportItem = reports[date]
        return {
          ...merchantDailyReportItem,
          key: index,
        }
      },
    )

    const reportData = reportsArray
    const income =
      (sumBy(reportData, 'codTotal') || 0) +
      (sumBy(reportData, 'bankTotal') || 0)
    const expense =
      (sumBy(reportData, 'costTotal') || 0) +
      (sumBy(reportData, 'commission') || 0) +
      (sumBy(reportData, 'costAmountTotal') || 0)

    const remaining = income - expense
    return (
      <>
        <CircularLoading isLoading={isLoading} />
        <Card>
          <CardHeader title="รายงานประจำวัน" />
          <Divider />
          <div ref={ref}>
            <PropertyList>
              <PropertyListItem
                align={`horizontal`}
                label="รายการสั่งซื้อ"
                value={`${numberFormat(sumBy(reportData, 'count'))} รายการ`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="จำนวนชิ้น"
                value={`${numberFormat(sumBy(reportData, 'unit'))} ชิ้น`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="ยอด COD"
                value={`${numberFormat(sumBy(reportData, 'codTotal'))} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="	จำนวน COD"
                value={`${numberFormat(sumBy(reportData, 'codCount'))} ชิ้น`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="ยอดโอน"
                value={`${numberFormat(sumBy(reportData, 'bankTotal'))} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="จำนวนโอน"
                value={`${numberFormat(sumBy(reportData, 'bankCount'))} ชิ้น`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="ค่าคอม"
                value={`${numberFormat(sumBy(reportData, 'commission'))} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="ต้นทุนสินค้า"
                value={`${user?.isNotAllowSeeCost ? '-' : numberFormat(sumBy(reportData, 'costTotal'))} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="ค่าใช้จ่าย"
                value={`${numberFormat(
                  sumBy(reportData, 'costAmountTotal'),
                )} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="รายรับ"
                value={`${numberFormat(income)} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="รายจ่าย"
                value={`${numberFormat(expense)} บาท`}
              />
              <Divider />
              <PropertyListItem
                align={`horizontal`}
                label="คงเหลือ"
                value={`${numberFormat(remaining)} บาท`}
              />
              <Divider />
            </PropertyList>
          </div>
        </Card>
      </>
    )
  },
)

export default DailyReportDetail
